import { TransactionTypeEnum } from '../../../../tenant/transactions/enums/transaction-type.enum';
import { ToSignedPaymentAmountParams } from '../interfaces/to-signed-payment-amount-params.interface';

/**
 * Signs a customer payment for a rental statement.
 *
 * Receipts and deposits are positive. Refunds are negative. Every other
 * transaction type is treated as money received and stays positive.
 * `amount` is used when it is set; otherwise `totalGross` is used.
 *
 * @param transaction A payment row. Any object with a transaction type and an amount qualifies.
 * @returns The signed amount. `null` and `undefined` amounts are `0`.
 * @example
 * ```ts
 * toSignedPaymentAmount({ type: TransactionTypeEnum.RECEIPT, amount: 40 }); // 40
 * toSignedPaymentAmount({ type: TransactionTypeEnum.REFUND, totalGross: 10 }); // -10
 * ```
 */
export function toSignedPaymentAmount<T extends ToSignedPaymentAmountParams>(
  transaction: T,
): number {
  const amount = Number(transaction.amount ?? transaction.totalGross ?? 0);

  switch (transaction.type) {
    case TransactionTypeEnum.REFUND:
      return -Math.abs(amount);
    case TransactionTypeEnum.DEPOSIT:
    case TransactionTypeEnum.RECEIPT:
    default:
      return Math.abs(amount);
  }
}
